// QuoteSystemsModal.tsx - Modal con feedback visual mejorado
import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import './QuoteModals.css';

interface FormData {
  empresa: string;
  correo: string;
  telefono: string;
  necesidad: string;
  observaciones: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const QuoteSystemsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    empresa: '',
    correo: '',
    telefono: '',
    necesidad: '',
    observaciones: ''
  });

  const validarFormulario = (): string | null => {
    if (!formData.empresa.trim()) return 'El nombre de la empresa es obligatorio';
    if (!formData.correo.trim()) return 'El correo es obligatorio';
    if (!formData.telefono.trim()) return 'El teléfono es obligatorio';
    if (!formData.necesidad.trim()) return 'Debes describir tu necesidad';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.correo)) {
      return 'El correo electrónico no es válido';
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    
    const error = validarFormulario();
    if (error) {
      setSubmitError(error);
      document.querySelector('.modal-content')?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/public/quote-systems', formData);

      if (response.data.success) {
        setSubmitSuccess(true);
        setSubmitError('');
        
        document.querySelector('.modal-content')?.scrollTo({ top: 0, behavior: 'smooth' });
        
        setTimeout(() => {
          resetFormulario();
          onClose();
          setSubmitSuccess(false);
        }, 3000);
      } else {
        throw new Error(response.data.error || 'Error al enviar consulta');
      }
    } catch (error: any) {
      console.error('Error enviando consulta:', error);
      setSubmitError(
        error.response?.data?.error || 
        error.message || 
        'Error al enviar la consulta. Intenta nuevamente.'
      );
      document.querySelector('.modal-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFormulario = () => {
    setFormData({
      empresa: '',
      correo: '',
      telefono: '',
      necesidad: '',
      observaciones: ''
    });
    setSubmitError('');
    setSubmitSuccess(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content systems-modal" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="modal-header">
          <h3>💼 Cotización de Sistemas Empresariales</h3>
          <button className="modal-close" onClick={onClose} type="button">
            <X />
          </button>
        </div>

        {/* SUCCESS MESSAGE */}
        {submitSuccess && (
          <div className="alert alert-success">
            <CheckCircle size={24} />
            <span>✅ Consulta enviada correctamente. Te contactaremos pronto.</span>
          </div>
        )}

        {/* ERROR MESSAGE */}
        {submitError && (
          <div className="alert alert-error">
            <AlertCircle size={24} />
            <span>{submitError}</span>
          </div>
        )}

        {/* FORM */}
        <form onSubmit={handleSubmit} className="systems-form">
          
          <div className="form-section">
            <h4 className="section-title">🏢 Información de la Empresa</h4>
            
            <div className="form-group">
              <label>Nombre de la Empresa *</label>
              <input
                type="text"
                value={formData.empresa}
                onChange={(e) => setFormData({...formData, empresa: e.target.value})}
                placeholder="Minera Los Andes S.A."
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Correo Electrónico *</label>
                <input
                  type="email"
                  value={formData.correo}
                  onChange={(e) => setFormData({...formData, correo: e.target.value})}
                  placeholder="contacto@empresa.com"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Teléfono *</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  placeholder="+56 9 1234 5678"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="section-title">📋 Descripción de la Necesidad</h4>
            
            <div className="form-group">
              <label>¿Qué necesitas? *</label>
              <textarea
                value={formData.necesidad}
                onChange={(e) => setFormData({...formData, necesidad: e.target.value})}
                rows={4}
                placeholder="Describe tu necesidad: sistema de gestión de flota, control de mantenimiento, integración con sistemas existentes, etc."
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label>Observaciones Adicionales</label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                rows={3}
                placeholder="Detalles adicionales, cantidad de equipos, urgencia, presupuesto estimado..."
                disabled={isSubmitting}
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-modal-submit" 
            disabled={isSubmitting || submitSuccess}
          >
            {isSubmitting ? 'Enviando...' : submitSuccess ? '✅ Enviado' : 'Enviar Consulta'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuoteSystemsModal;